import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { errMsg } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const TOKEN_KEY = "g39_token";
const CK = "g39.company";
const BK = "g39.branch";
const SessionCtx = createContext(null);

export function SessionProvider({ children }) {
  const sel = useRef({ companyId: localStorage.getItem(CK) || "", branchId: localStorage.getItem(BK) || "" });
  const [state, setState] = useState({ loading: true, me: null, error: null });
  const [scope, setScope] = useState({ companyId: "", branchId: "", branches: [], v: 0 });

  const api = useMemo(() => {
    const inst = axios.create({ baseURL: API });
    inst.interceptors.request.use(async (cfg) => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        cfg.headers.Authorization = `Bearer ${token}`;
      }
      if (!cfg.noScope && sel.current.companyId) cfg.headers["X-Company-Id"] = sel.current.companyId;
      if (!cfg.noScope && !cfg.noBranch && sel.current.branchId) cfg.headers["X-Branch-Id"] = sel.current.branchId;
      return cfg;
    });
    return inst;
  }, []);

  const applyScope = useCallback(async (companyId, branchId) => {
    sel.current = { companyId, branchId: "" };
    let branches = [];
    if (companyId) {
      try {
        branches = (await api.get("/branches", { noBranch: true })).data;
      } catch {
        branches = [];
      }
    }
    const bid = branches.some((b) => b.id === branchId) ? branchId : "";
    sel.current = { companyId, branchId: bid };
    localStorage.setItem(CK, companyId);
    localStorage.setItem(BK, bid);
    setScope((s) => ({ companyId, branchId: bid, branches, v: s.v + 1 }));
  }, [api]);

  const reload = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setState({ loading: false, me: null, error: null });
      return;
    }
    try {
      const { data } = await api.get("/me", { noScope: true });
      if (data.provisioned) {
        const ids = data.companies.map((c) => c.id);
        const isPlatform = data.role === "platform_admin";
        let cid = sel.current.companyId;
        if (!ids.includes(cid)) cid = isPlatform ? "" : data.user.company_id || ids[0] || "";
        await applyScope(cid, sel.current.branchId);
      }
      setState({ loading: false, me: data, error: null });
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setState({ loading: false, me: null, error: null });
      } else {
        setState({ loading: false, me: null, error: errMsg(e) });
      }
    }
  }, [api, applyScope]);

  useEffect(() => {
    reload();
  }, [reload]);

  const login = useCallback(async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    if (res.data?.token) {
      localStorage.setItem(TOKEN_KEY, res.data.token);
      await reload();
    }
    return res.data;
  }, [reload]);

  const register = useCallback(async (data) => {
    const res = await axios.post(`${API}/auth/register`, data);
    if (res.data?.token) {
      localStorage.setItem(TOKEN_KEY, res.data.token);
      await reload();
    }
    return res.data;
  }, [reload]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setState({ loading: false, me: null, error: null });
    window.location.href = "/sign-in";
  }, []);

  const value = useMemo(() => {
    const me = state.me;
    const perms = new Set(me?.permissions || []);
    const company = me?.companies?.find((c) => c.id === scope.companyId) || null;
    return {
      ...state,
      api,
      reload,
      login,
      register,
      logout,
      companyId: scope.companyId,
      branchId: scope.branchId,
      branches: scope.branches,
      branch: scope.branches.find((b) => b.id === scope.branchId) || null,
      company,
      scopeKey: `${scope.companyId}|${scope.branchId}|${scope.v}`,
      isPlatform: me?.role === "platform_admin",
      can: (p) => perms.has(p),
      setCompany: (cid) => applyScope(cid, ""),
      setBranch: (bid) => applyScope(scope.companyId, bid),
      refreshBranches: () => applyScope(scope.companyId, scope.branchId),
    };
  }, [state, scope, api, reload, applyScope, login, register, logout]);

  return <SessionCtx.Provider value={value}>{children}</SessionCtx.Provider>;
}

export function useSession() {
  return useContext(SessionCtx);
}
